import React, { useState, useEffect } from 'react';
import Nango from '@nangohq/frontend';
import { CheckCircleIcon, ErrorIcon } from './simple-icons';

interface NangoAuthProps {
  providerConfigKey: string;
  connectionId: string;
  onConnectionSuccess: (result: { providerConfigKey: string; connectionId: string; }) => void;
  onConnectionError: (error: Error) => void;
}

const NangoAuth: React.FC<NangoAuthProps> = ({ 
  providerConfigKey, 
  connectionId,
  onConnectionSuccess,
  onConnectionError 
}) => {
  const [nango, setNango] = useState<Nango | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isConnecting, setIsConnecting] = useState(false);

  useEffect(() => {
    const nangoHost = process.env.REACT_APP_NANGO_HOST?.trim();
    const nangoPublicKey = process.env.REACT_APP_NANGO_PUBLIC_KEY?.trim();

    if (!nangoHost || !nangoPublicKey) {
      console.error('[NangoAuth] Nango environment variables are not set.');
      setError('Configuration error: Nango environment variables are missing.');
      return;
    }

    try {
      const nangoInstance = new Nango({ 
        host: nangoHost, 
        publicKey: nangoPublicKey 
      });
      setNango(nangoInstance);
    } catch (err) {
      console.error('[NangoAuth] Failed to initialize Nango:', err);
      setError('Failed to initialize authentication service.');
    }
  }, []);

  const handleConnect = async () => {
    if (!nango) {
      console.error('[NangoAuth] Nango is not initialized. Aborting auth.');
      setError('Authentication service is not ready.');
      return;
    }

    setIsConnecting(true);
    setError(null);

    try {
      // Pass provider-specific parameters, which are required for some providers like Microsoft.
      const result = await nango.auth(providerConfigKey, connectionId, {
        params: { tenant: 'common' }
      });
      console.log('[NangoAuth] Nango auth successful:', result);
      setIsConnected(true);
      onConnectionSuccess(result);
    } catch (err) {
      console.error('[NangoAuth] Nango auth error:', err);
      const authError = err as Error;
      setError(`Connection failed: ${authError.message}`);
      onConnectionError(authError);
    } finally {
      setIsConnecting(false);
    }
  };

  if (error) {
    return (
      <div className="flex items-center space-x-2 text-red-600">
        <ErrorIcon />
        <span className="text-sm">{error}</span>
      </div>
    );
  }

  if (isConnected) {
    return (
      <div className="flex items-center space-x-2 text-green-600">
        <CheckCircleIcon />
        <span className="text-sm font-medium">Connected</span>
      </div>
    );
  }

  return (
    <button
      onClick={handleConnect}
      disabled={isConnecting || !nango}
      className="px-4 py-2 bg-netlify-teal text-white rounded-lg hover:bg-netlify-600 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
    >
      {isConnecting ? 'Connecting...' : 'Connect'}
    </button>
  );
};

export default NangoAuth;
